import { useEffect, useRef } from "react";

/**
 * Who should re-read their data when the traveller asks for fresh data.
 *
 * A pull at the top of the screen means "show me what is there now", and the thing that knows
 * what "there" is, is whatever is currently on screen - not the shell that drew the spinner. So
 * every loader registers itself while it is mounted, and the pull simply asks all of them.
 *
 * A module-level set rather than a context: the registrations outlive any one render, the shell
 * never needs to re-render because a page started loading something, and a loader deep inside a
 * sheet can join without anything in between having to pass a prop down to it.
 */

type Loader = () => Promise<unknown>;

const loaders = new Set<Loader>();

/** Registers a loader until it is unregistered. Returns the unregister. */
export function onRefresh(load: Loader): () => void {
  loaders.add(load);
  return () => {
    loaders.delete(load);
  };
}

/**
 * Asks everything on screen to re-read. Settles when they all have, however they went: one list
 * failing is that list's error to show, not a reason to leave the spinner turning forever.
 */
export async function refreshAll(): Promise<void> {
  await Promise.allSettled([...loaders].map((load) => load()));
}

/**
 * Registers a loader for as long as the component is mounted.
 *
 * The function is held in a ref and the subscription is made once, so a loader that is rebuilt
 * on every render - which `useCallback` dependencies make common - does not churn the set, and
 * the pull always calls the newest one.
 */
export function useRefreshable(load: Loader): void {
  const latest = useRef(load);
  latest.current = load;
  useEffect(() => onRefresh(() => latest.current()), []);
}
